/**
 * Журнал волны — то, что клиент отправляет серверу после каждой волны (античит, MADR 0002):
 * сид, номер волны, версия баланса, состояние забежки на старте, команды с тиками и итог.
 *
 * Сервер (S2) берёт `start` из своей базы (а не из журнала!) и сверяет его с присланным, прогоняет engine с сидом,
 * который выдал сам (`NextWave.seed`), применяет `commands` и сравнивает свой {@link WaveResult} с присланным.
 * Расхождение — прав сервер: ответ несёт подтверждённое состояние, клиент откатывается (I2).
 * Идемпотентность — по `(runId, wave)`: повтор того же журнала возвращает тот же ответ.
 */
import { z } from "zod";
import { BalanceVersion } from "./balance";
import { TimedCommand } from "./commands";
import { Coins, ContentId, HeroId, PlatformSlot, PosInt, Seed, Ticks, UInt } from "./common";

/** Версия формата журнала. Меняется при несовместимой правке схемы. */
export const WAVE_LOG_FORMAT = 1;

/** Герой в составе забежки. */
export const RunHero = z.object({
  heroId: HeroId,
  slot: PlatformSlot,
  level: PosInt,
  stars: z.number().int().min(0).max(5),
});
export type RunHero = z.infer<typeof RunHero>;

/** Взятое усиление и число его стаков. */
export const RunBoost = z.object({
  boostId: ContentId,
  stacks: PosInt,
});
export type RunBoost = z.infer<typeof RunBoost>;

/**
 * Состояние забежки на старте волны — всё, от чего зависит бой, кроме сида и команд.
 * Забежка живёт до проигрыша или выхода (закрыл / свёрнуто > 10 мин); хранится на сервере.
 */
export const RunState = z.object({
  /** Идентификатор забежки (UUID, выдаёт сервер). */
  runId: z.uuid(),
  /** Волна, которая сейчас начнётся. */
  wave: PosInt,
  /** Герои на платформах (уровни и звёзды — из прогресса игрока). */
  heroes: z.array(RunHero).max(16),
  /** Уровни Tech: `techId → уровень`. */
  tech: z.record(ContentId, UInt),
  /** Усиления «1 из 3», взятые в этой забежке. */
  boosts: z.array(RunBoost),
  /** Прочность штаба на старте волны (между волнами не восстанавливается полностью — решает engine). */
  hqHp: UInt,
  /** Состояние переключателя «Автонавыки». */
  autoSkills: z.boolean(),
});
export type RunState = z.infer<typeof RunState>;

/** Исход волны. */
export const WaveOutcome = z.enum(["cleared", "lost"]);
export type WaveOutcome = z.infer<typeof WaveOutcome>;

/** Итог волны: клиент присылает свой, сервер считает свой и сравнивает поля. */
export const WaveResult = z.object({
  outcome: WaveOutcome,
  /** Сколько тиков длилась волна. */
  ticks: Ticks,
  kills: UInt,
  /** Монеты за волну (убийства + награда за волну). */
  coins: Coins,
  /** Прочность штаба в конце. */
  hqHp: UInt,
  /** Босс убит (только для волн с боссом). */
  bossKilled: z.boolean(),
  /**
   * Хэш итогового состояния engine (hex, 8–64 символа) — быстрая сверка «всё ли совпало»;
   * функцию хэша задаёт E1 и не меняет без смены {@link WAVE_LOG_FORMAT}.
   */
  stateHash: z.string().regex(/^[0-9a-f]{8,64}$/),
});
export type WaveResult = z.infer<typeof WaveResult>;

/** Журнал одной волны. */
export const WaveLog = z
  .object({
    format: z.literal(WAVE_LOG_FORMAT),
    runId: z.uuid(),
    wave: PosInt,
    seed: Seed,
    balanceVersion: BalanceVersion,
    /** Версия клиента (git SHA сборки) — для разбора расхождений. */
    clientVersion: z.string().min(1).max(64),
    start: RunState,
    /**
     * Клиент играл волну с VIP: автонавыки разрешены. Сервер верит флагу, только если срок VIP не кончился к началу волны
     * (MADR 0003). Поле нужно, потому что VIP может быть куплен посреди волны.
     */
    vip: z.boolean().optional(),
    /** Команды по возрастанию тика; на одном тике — в порядке нажатия. Не больше 2000 на волну. */
    commands: z.array(TimedCommand).max(2000),
    result: WaveResult,
  })
  .superRefine((log, ctx) => {
    if (log.start.runId !== log.runId) ctx.addIssue({ code: "custom", message: "start.runId != runId" });
    if (log.start.wave !== log.wave) ctx.addIssue({ code: "custom", message: "start.wave != wave" });
    for (let i = 1; i < log.commands.length; i++) {
      if (log.commands[i]!.tick < log.commands[i - 1]!.tick) {
        ctx.addIssue({ code: "custom", message: `commands[${i}]: ticks must be non-decreasing` });
        break;
      }
    }
    const choices = log.commands.filter((c) => c.command.type === "chooseBoost");
    if (choices.length > 1) ctx.addIssue({ code: "custom", message: "at most one chooseBoost per wave" });
    if (choices.length === 1 && log.result.outcome !== "cleared") {
      ctx.addIssue({ code: "custom", message: "chooseBoost only after a cleared wave" });
    }
  });
export type WaveLog = z.infer<typeof WaveLog>;
